import { apiFetch, ApiError } from "./client";
import { newIdempotencyKey } from "./idempotency";
import type {
  AddItemToTabRequest,
  CashSessionDetailsDto,
  CashSessionDto,
  CashTransactionDto,
  CalculateCourtBookingPriceRequest,
  CancelBookingRequest,
  CalculatedCourtPriceDto,
  CategoryDto,
  CloseCashSessionRequest,
  CloseTabRequest,
  CreateMemberRequest,
  UpdateMemberRequest,
  CreateCategoryRequest,
  CreateProductRequest,
  CreateQuickSaleRequest,
  CreatePriceRuleRequest,
  CreateCourtRequest,
  CreateCourtBlockRequest,
  CourtBlockDto,
  CourtDto,
  CourtBookingDto,
  CourtScheduleDto,
  ClassScheduleDto,
  ClassDto,
  ClassMemberDetailsDto,
  NextClassSuggestionDto,
  ClassSessionsResponse,
  UpdateClassSessionRequest,
  CreateClassRequest,
  UpdateClassRequest,
  CreateWalkInBookingRequest,
  MemberAutocompleteDto,
  MemberDto,
  MemberProfileDto,
  MemberAttendanceSummaryDto,
  OpenCashSessionRequest,
  OpenTabRequest,
  OperatingHourDto,
  PagedResult,
  PagedResultOfSaleDto,
  PagedResultOfCourtBlockDto,
  PagedResultOfMemberActivityHistoryItemDto,
  ProductDto,
  RecordCashTransactionRequest,
  SaleDto,
  SetOperatingHoursRequest,
  TabDetailsDto,
  TenantMembershipDto,
  PriceRuleDto,
  UpdatePriceRuleRequest,
  UpdateCourtRequest,
  SportDto,
  CreateSportRequest,
  UpdateSportRequest,
  PlanDto,
  CreatePlanRequest,
  UpdatePlanRequest,
  PagedResultOfPlanDto,
  ExpenseDto,
  CreateExpenseRequest,
  UpdateExpenseRequest,
  PayExpenseRequest,
  UpdateExpenseSeriesRequest,
  CreateExpenseResultDto,
  PagedResultOfExpenseDto,
  FinancialSummaryDto,
  MonthlyProfitPointDto,
  MarginByClassDto,
  OverdueMembershipPaymentDto,
  MembershipPaymentDto,
  TaskItemDto,
  CreateTaskItemRequest,
  UpdateTaskItemRequest,
  PagedResultOfTaskItemDto,
  TenantFinancialSettingsDto,
  UpdateTenantFinancialSettingsRequest,
  CheckInDto,
  CheckInRequest,
  CheckInSummaryDto,
  PagedResultOfDailyCheckInStatusDto,
  PagedResultOfTenantMembershipPaymentDto,
  MeTenantsResponse,
  InviteValidationDto,
  AcceptInviteRequest,
  AcceptInviteResult,
  InviteDto,
  TenantInviteDto,
  TenantMemberDto,
  PatchNoteDto,
  PagedResultOfPatchNoteDto,
  NotificationsPageDto,
  MarkNotificationsReadResult,
} from "../../types";

/**
 * Prefixos por grupo de rota da apogeu-api.
 * Cada grupo declara sua versão de forma independente — migrar um grupo para
 * `/v2` significa trocar o prefixo dele aqui, sem afetar os demais.
 */
export const GROUPS = {
  ME: "/api/me",
  TENANTS: "/api/tenants",
  TEST_AUTH: "/api/TestAuth",
  INVITES: "/api/invites",
  PUBLIC_INVITES: "/api/public/invites",
  V1: {
    PRODUCTS: "/api/v1/products",
    SALES: "/api/v1/sales",
    CASH_SESSIONS: "/api/v1/cash-sessions",
    CATEGORIES: "/api/v1/categories",
    MEMBERS: "/api/v1/members",
    COURTS: "/api/v1/courts",
    COURT_BLOCKS: "/api/v1/court-blocks",
    OPERATING_HOURS: "/api/v1/operating-hours",
    PRICE_RULES: "/api/v1/price-rules",
    BOOKINGS: "/api/v1/bookings",
    CLASSES: "/api/v1/classes",
    SPORTS: "/api/v1/sports",
    PLANS: "/api/v1/plans",
    EXPENSES: "/api/v1/expenses",
    FINANCIALS: "/api/v1/financials",
    MEMBER_PAYMENTS: "/api/v1/members/payments",
    TASKS: "/api/v1/tasks",
    CHECK_INS: "/api/v1/checkins",
    FINANCIAL_SETTINGS: "/api/v1/financial-settings",
    PATCH_NOTES: "/api/v1/patch-notes",
    NOTIFICATIONS: "/api/v1/notifications",
  },
} as const;

export async function fetchMyTenants(token: string): Promise<MeTenantsResponse> {
  return apiFetch<MeTenantsResponse>(`${GROUPS.ME}/tenants`, { token });
}

export async function fetchInviteValidation(code: string): Promise<InviteValidationDto> {
  return apiFetch<InviteValidationDto>(`${GROUPS.PUBLIC_INVITES}/${code}`);
}

export async function acceptInvite(token: string, code: string, payload: AcceptInviteRequest): Promise<AcceptInviteResult> {
  return apiFetch<AcceptInviteResult>(`${GROUPS.INVITES}/${code}/accept`, { token, method: "POST", body: payload });
}

export async function createTeamInvite(token: string, email: string, roleId: string): Promise<InviteDto> {
  const query = new URLSearchParams({ email, roleId }).toString();
  return apiFetch<InviteDto>(`${GROUPS.TENANTS}/invites?${query}`, { token, method: "POST" });
}

export async function listTenantInvites(token: string): Promise<TenantInviteDto[]> {
  return apiFetch<TenantInviteDto[]>(`${GROUPS.TENANTS}/invites`, { token });
}

export async function listTenantMembers(token: string): Promise<TenantMemberDto[]> {
  return apiFetch<TenantMemberDto[]>(`${GROUPS.TENANTS}/members`, { token });
}

export async function updateMemberRole(token: string, userId: string, roleId: string): Promise<void> {
  await apiFetch<unknown>(`${GROUPS.TENANTS}/members/${userId}`, { token, method: "PATCH", body: { roleId } });
}

export async function removeTenantMember(token: string, userId: string): Promise<void> {
  await apiFetch<unknown>(`${GROUPS.TENANTS}/members/${userId}`, { token, method: "DELETE" });
}

export async function switchTenant(token: string, tenantId: string): Promise<void> {
  await apiFetch<unknown>(`${GROUPS.TENANTS}/${tenantId}/switch`, { token, method: "POST" });
}

export async function fetchTestAuthMe(token: string): Promise<{ userId: string; email: string }> {
  return apiFetch<{ userId: string; email: string }>(`${GROUPS.TEST_AUTH}/me`, { token });
}

export interface ListProductsParams {
  page?: number;
  pageSize?: number;
  search?: string;
  categoryId?: string;
  isActive?: boolean;
}

function toQuery<T extends object>(params: T): string {
  const q = new URLSearchParams();
  for (const [key, value] of Object.entries(params)) {
    if (Array.isArray(value)) {
      value.forEach(item => q.append(key, String(item)));
      continue;
    }
    if (value !== undefined && value !== "") q.set(key, String(value));
  }
  const s = q.toString();
  return s ? `?${s}` : "";
}

export async function listProducts(token: string, params: ListProductsParams = {}): Promise<PagedResult<ProductDto>> {
  return apiFetch<PagedResult<ProductDto>>(`${GROUPS.V1.PRODUCTS}${toQuery(params)}`, { token });
}

export async function getProduct(token: string, productId: string): Promise<ProductDto> {
  return apiFetch<ProductDto>(`${GROUPS.V1.PRODUCTS}/${productId}`, { token });
}

export async function createProduct(token: string, payload: CreateProductRequest): Promise<ProductDto> {
  return apiFetch<ProductDto>(GROUPS.V1.PRODUCTS, { token, method: "POST", body: payload });
}

export async function updateProduct(token: string, productId: string, payload: CreateProductRequest): Promise<ProductDto> {
  return apiFetch<ProductDto>(`${GROUPS.V1.PRODUCTS}/${productId}`, { token, method: "PUT", body: payload });
}

export async function deleteProduct(token: string, productId: string): Promise<void> {
  await apiFetch<unknown>(`${GROUPS.V1.PRODUCTS}/${productId}`, { token, method: "DELETE" });
}

/**
 * Upload full-replace da foto do produto. O mesmo endpoint serve create e edit;
 * o upload não tem Idempotency-Key (IFormFile não é serializável no hash).
 */
export async function uploadProductImage(token: string, productId: string, file: File): Promise<ProductDto> {
  const form = new FormData();
  form.append("file", file);
  return apiFetch<ProductDto>(`${GROUPS.V1.PRODUCTS}/${productId}/image`, { token, method: "POST", body: form });
}

export async function listCategories(token: string): Promise<CategoryDto[]> {
  return apiFetch<CategoryDto[]>(GROUPS.V1.CATEGORIES, { token });
}

export async function createCategory(token: string, payload: CreateCategoryRequest): Promise<CategoryDto> {
  return apiFetch<CategoryDto>(GROUPS.V1.CATEGORIES, { token, method: "POST", body: payload });
}

export async function updateCategory(token: string, categoryId: string, payload: CreateCategoryRequest): Promise<CategoryDto> {
  return apiFetch<CategoryDto>(`${GROUPS.V1.CATEGORIES}/${categoryId}`, { token, method: "PUT", body: payload });
}

export async function deleteCategory(token: string, categoryId: string): Promise<void> {
  await apiFetch<unknown>(`${GROUPS.V1.CATEGORIES}/${categoryId}`, { token, method: "DELETE" });
}

export interface ListMembersParams {
  page?: number;
  pageSize?: number;
  search?: string;
  enrollmentTypes?: number[];
}

export async function listMembers(token: string, params: ListMembersParams = {}): Promise<PagedResult<MemberDto>> {
  return apiFetch<PagedResult<MemberDto>>(`${GROUPS.V1.MEMBERS}${toQuery(params)}`, { token });
}

export async function createMember(token: string, payload: CreateMemberRequest): Promise<MemberProfileDto> {
  return apiFetch<MemberProfileDto>(GROUPS.V1.MEMBERS, { token, method: "POST", body: payload });
}

export async function updateMember(token: string, memberId: string, payload: UpdateMemberRequest): Promise<MemberProfileDto> {
  return apiFetch<MemberProfileDto>(`${GROUPS.V1.MEMBERS}/${memberId}`, { token, method: "PUT", body: payload });
}

export async function getMemberProfile(token: string, memberId: string): Promise<MemberProfileDto> {
  return apiFetch<MemberProfileDto>(`${GROUPS.V1.MEMBERS}/${memberId}/profile`, { token });
}

export async function deleteMember(token: string, memberId: string): Promise<void> {
  await apiFetch<unknown>(`${GROUPS.V1.MEMBERS}/${memberId}`, { token, method: "DELETE" });
}

export interface SearchMembersAutocompleteParams {
  q: string;
  limit?: number;
}

export async function searchMembersAutocomplete(token: string, params: SearchMembersAutocompleteParams): Promise<MemberAutocompleteDto[]> {
  return apiFetch<MemberAutocompleteDto[]>(`${GROUPS.V1.MEMBERS}/autocomplete${toQuery(params)}`, { token });
}

export interface MemberActivityHistoryParams {
  from?: string;
  to?: string;
  status?: number;
  page?: number;
  pageSize?: number;
}

export async function getMemberActivityHistory(token: string, memberId: string, params: MemberActivityHistoryParams = {}): Promise<PagedResultOfMemberActivityHistoryItemDto> {
  return apiFetch<PagedResultOfMemberActivityHistoryItemDto>(`${GROUPS.V1.MEMBERS}/${memberId}/activity-history${toQuery(params)}`, { token });
}

export interface MemberAttendanceSummaryParams {
  from?: string;
  to?: string;
}

export async function getMemberAttendanceSummary(token: string, memberId: string, params: MemberAttendanceSummaryParams = {}): Promise<MemberAttendanceSummaryDto> {
  return apiFetch<MemberAttendanceSummaryDto>(`${GROUPS.V1.MEMBERS}/${memberId}/attendance-summary${toQuery(params)}`, { token });
}

export interface ListCourtsParams {
  page?: number;
  pageSize?: number;
  sportType?: number;
  surfaceType?: number;
  isCovered?: boolean;
  isActive?: boolean;
}

export async function listCourts(token: string, params: ListCourtsParams = {}): Promise<PagedResult<CourtDto>> {
  return apiFetch<PagedResult<CourtDto>>(`${GROUPS.V1.COURTS}${toQuery(params)}`, { token });
}

export async function getCourt(token: string, courtId: string): Promise<CourtDto> {
  return apiFetch<CourtDto>(`${GROUPS.V1.COURTS}/${courtId}`, { token });
}

export async function createCourt(token: string, payload: CreateCourtRequest, idempotencyKey?: string): Promise<CourtDto> {
  return apiFetch<CourtDto>(GROUPS.V1.COURTS, { token, method: "POST", body: payload, headers: idempotencyHeaders(idempotencyKey) });
}

export async function updateCourt(token: string, courtId: string, payload: UpdateCourtRequest, idempotencyKey?: string): Promise<CourtDto> {
  return apiFetch<CourtDto>(`${GROUPS.V1.COURTS}/${courtId}`, { token, method: "PATCH", body: payload, headers: idempotencyHeaders(idempotencyKey) });
}

export async function toggleCourtStatus(token: string, courtId: string, isActive: boolean, idempotencyKey?: string): Promise<CourtDto> {
  return apiFetch<CourtDto>(`${GROUPS.V1.COURTS}/${courtId}/toggle-status`, { token, method: "PATCH", body: { isActive }, headers: idempotencyHeaders(idempotencyKey) });
}

export async function deleteCourt(token: string, courtId: string, idempotencyKey?: string): Promise<void> {
  await apiFetch<unknown>(`${GROUPS.V1.COURTS}/${courtId}`, { token, method: "DELETE", headers: idempotencyHeaders(idempotencyKey) });
}

export interface ListCourtBlocksParams {
  page?: number;
  pageSize?: number;
  startTime?: string;
  endTime?: string;
  courtId?: string;
}

export async function listCourtBlocks(token: string, params: ListCourtBlocksParams = {}): Promise<PagedResultOfCourtBlockDto> {
  return apiFetch<PagedResultOfCourtBlockDto>(`${GROUPS.V1.COURT_BLOCKS}${toQuery(params)}`, { token });
}

export async function createCourtBlock(token: string, payload: CreateCourtBlockRequest, idempotencyKey?: string): Promise<CourtBlockDto> {
  return apiFetch<CourtBlockDto>(GROUPS.V1.COURT_BLOCKS, { token, method: "POST", body: payload, headers: idempotencyHeaders(idempotencyKey) });
}

export async function deleteCourtBlock(token: string, blockId: string, idempotencyKey?: string): Promise<void> {
  await apiFetch<unknown>(`${GROUPS.V1.COURT_BLOCKS}/${blockId}`, { token, method: "DELETE", headers: idempotencyHeaders(idempotencyKey) });
}

export interface ListSportsParams {
  page?: number;
  pageSize?: number;
  isActive?: boolean;
}

export async function listSports(token: string, params: ListSportsParams = {}): Promise<PagedResult<SportDto>> {
  return apiFetch<PagedResult<SportDto>>(`${GROUPS.V1.SPORTS}${toQuery(params)}`, { token });
}

export async function createSport(token: string, payload: CreateSportRequest, idempotencyKey?: string): Promise<SportDto> {
  return apiFetch<SportDto>(GROUPS.V1.SPORTS, { token, method: "POST", body: payload, headers: idempotencyHeaders(idempotencyKey) });
}

export async function updateSport(token: string, sportId: string, payload: UpdateSportRequest, idempotencyKey?: string): Promise<SportDto> {
  return apiFetch<SportDto>(`${GROUPS.V1.SPORTS}/${sportId}`, { token, method: "PATCH", body: payload, headers: idempotencyHeaders(idempotencyKey) });
}

export async function toggleSportStatus(token: string, sportId: string, isActive: boolean, idempotencyKey?: string): Promise<SportDto> {
  return apiFetch<SportDto>(`${GROUPS.V1.SPORTS}/${sportId}/toggle-status`, { token, method: "PATCH", body: { isActive }, headers: idempotencyHeaders(idempotencyKey) });
}

export async function deleteSport(token: string, sportId: string, idempotencyKey?: string): Promise<void> {
  await apiFetch<unknown>(`${GROUPS.V1.SPORTS}/${sportId}`, { token, method: "DELETE", headers: idempotencyHeaders(idempotencyKey) });
}

export interface ListPlansParams {
  page?: number;
  pageSize?: number;
  isActive?: boolean;
  billingPeriod?: number;
}

export async function listPlans(token: string, params: ListPlansParams = {}): Promise<PagedResultOfPlanDto> {
  return apiFetch<PagedResultOfPlanDto>(`${GROUPS.V1.PLANS}${toQuery(params)}`, { token });
}

export async function createPlan(token: string, payload: CreatePlanRequest, idempotencyKey?: string): Promise<PlanDto> {
  return apiFetch<PlanDto>(GROUPS.V1.PLANS, { token, method: "POST", body: payload, headers: idempotencyHeaders(idempotencyKey) });
}

export async function updatePlan(token: string, planId: string, payload: UpdatePlanRequest, idempotencyKey?: string): Promise<PlanDto> {
  return apiFetch<PlanDto>(`${GROUPS.V1.PLANS}/${planId}`, { token, method: "PATCH", body: payload, headers: idempotencyHeaders(idempotencyKey) });
}

export async function togglePlanStatus(token: string, planId: string, isActive: boolean, idempotencyKey?: string): Promise<PlanDto> {
  return apiFetch<PlanDto>(`${GROUPS.V1.PLANS}/${planId}/toggle-status`, { token, method: "PATCH", body: { isActive }, headers: idempotencyHeaders(idempotencyKey) });
}

export async function deletePlan(token: string, planId: string, idempotencyKey?: string): Promise<void> {
  await apiFetch<unknown>(`${GROUPS.V1.PLANS}/${planId}`, { token, method: "DELETE", headers: idempotencyHeaders(idempotencyKey) });
}

export async function listOperatingHours(token: string): Promise<OperatingHourDto[]> {
  return apiFetch<OperatingHourDto[]>(GROUPS.V1.OPERATING_HOURS, { token });
}

export async function setOperatingHours(
  token: string,
  payload: SetOperatingHoursRequest,
  idempotencyKey?: string,
): Promise<OperatingHourDto[]> {
  return apiFetch<OperatingHourDto[]>(GROUPS.V1.OPERATING_HOURS, {
    token,
    method: "PUT",
    body: payload,
    headers: idempotencyHeaders(idempotencyKey),
  });
}

export interface ListPriceRulesParams {
  page?: number;
  pageSize?: number;
  courtId?: string;
  isActive?: boolean;
}

export async function listPriceRules(token: string, params: ListPriceRulesParams = {}): Promise<PagedResult<PriceRuleDto>> {
  return apiFetch<PagedResult<PriceRuleDto>>(`${GROUPS.V1.PRICE_RULES}${toQuery(params)}`, { token });
}

export async function createPriceRule(
  token: string,
  payload: CreatePriceRuleRequest,
  idempotencyKey?: string,
): Promise<PriceRuleDto> {
  return apiFetch<PriceRuleDto>(GROUPS.V1.PRICE_RULES, {
    token,
    method: "POST",
    body: payload,
    headers: idempotencyHeaders(idempotencyKey),
  });
}

export async function updatePriceRule(
  token: string,
  priceRuleId: string,
  payload: UpdatePriceRuleRequest,
  idempotencyKey?: string,
): Promise<PriceRuleDto> {
  return apiFetch<PriceRuleDto>(`${GROUPS.V1.PRICE_RULES}/${priceRuleId}`, {
    token,
    method: "PATCH",
    body: payload,
    headers: idempotencyHeaders(idempotencyKey),
  });
}

export async function deletePriceRule(token: string, priceRuleId: string, idempotencyKey?: string): Promise<void> {
  await apiFetch<unknown>(`${GROUPS.V1.PRICE_RULES}/${priceRuleId}`, {
    token,
    method: "DELETE",
    headers: idempotencyHeaders(idempotencyKey),
  });
}

export async function calculateCourtPrice(
  token: string,
  payload: CalculateCourtBookingPriceRequest,
): Promise<CalculatedCourtPriceDto> {
  return apiFetch<CalculatedCourtPriceDto>(`${GROUPS.V1.PRICE_RULES}/calculate`, {
    token,
    method: "POST",
    body: payload,
  });
}

export interface BookingScheduleParams {
  date: string;
  courtId?: string;
}

export async function getBookingSchedule(token: string, params: BookingScheduleParams): Promise<CourtScheduleDto> {
  return apiFetch<CourtScheduleDto>(`${GROUPS.V1.BOOKINGS}/schedule${toQuery(params)}`, { token });
}

export interface ClassScheduleParams {
  weekStart: string;
  courtId: string;
}

export async function getClassSchedule(token: string, params: ClassScheduleParams): Promise<ClassScheduleDto> {
  return apiFetch<ClassScheduleDto>(`${GROUPS.V1.CLASSES}/schedule${toQuery(params)}`, { token });
}

export interface ListClassesParams {
  isActive?: boolean;
  dayOfWeek?: number;
  sportId?: string;
  page?: number;
  pageSize?: number;
}

export async function listClasses(token: string, params: ListClassesParams = {}): Promise<PagedResult<ClassDto>> {
  return apiFetch<PagedResult<ClassDto>>(`${GROUPS.V1.CLASSES}${toQuery(params)}`, { token });
}

export async function createClass(token: string, payload: CreateClassRequest, idempotencyKey?: string): Promise<ClassDto> {
  return apiFetch<ClassDto>(GROUPS.V1.CLASSES, { token, method: "POST", body: payload, headers: idempotencyHeaders(idempotencyKey) });
}

export async function updateClass(token: string, classId: string, payload: UpdateClassRequest, idempotencyKey?: string): Promise<ClassDto> {
  return apiFetch<ClassDto>(`${GROUPS.V1.CLASSES}/${classId}`, { token, method: "PATCH", body: payload, headers: idempotencyHeaders(idempotencyKey) });
}

export async function deleteClass(token: string, classId: string, idempotencyKey?: string): Promise<void> {
  await apiFetch<unknown>(`${GROUPS.V1.CLASSES}/${classId}`, { token, method: "DELETE", headers: idempotencyHeaders(idempotencyKey) });
}

export async function addClassMember(token: string, classId: string, memberId: string, idempotencyKey?: string): Promise<ClassDto> {
  return apiFetch<ClassDto>(`${GROUPS.V1.CLASSES}/${classId}/members`, { token, method: "POST", body: { memberId }, headers: idempotencyHeaders(idempotencyKey) });
}

export async function removeClassMember(token: string, classId: string, memberId: string, idempotencyKey?: string): Promise<ClassDto> {
  return apiFetch<ClassDto>(`${GROUPS.V1.CLASSES}/${classId}/members/${memberId}`, { token, method: "DELETE", headers: idempotencyHeaders(idempotencyKey) });
}

export async function getClassMembers(token: string, classId: string): Promise<ClassMemberDetailsDto[]> {
  return apiFetch<ClassMemberDetailsDto[]>(`${GROUPS.V1.CLASSES}/${classId}/members`, { token });
}

export async function getNextClassSuggestion(token: string, classId: string): Promise<NextClassSuggestionDto | null> {
  return apiFetch<NextClassSuggestionDto | null>(`${GROUPS.V1.CLASSES}/${classId}/next-available`, { token });
}

export interface ListClassSessionsParams {
  startDate?: string;
  endDate?: string;
  courtId?: string;
}

export async function listClassSessions(token: string, params: ListClassSessionsParams = {}): Promise<ClassSessionsResponse> {
  return apiFetch<ClassSessionsResponse>(`/api/v1/class-sessions${toQuery(params)}`, { token });
}

export async function materializeClassSessions(token: string, idempotencyKey?: string): Promise<number> {
  return apiFetch<number>("/api/v1/class-sessions/materialize", { token, method: "POST", headers: idempotencyHeaders(idempotencyKey) });
}

export async function updateClassSession(token: string, sessionId: string, payload: UpdateClassSessionRequest, idempotencyKey?: string): Promise<void> {
  await apiFetch<unknown>(`/api/v1/class-sessions/${sessionId}`, { token, method: "PATCH", body: payload, headers: idempotencyHeaders(idempotencyKey) });
}

export async function getBooking(token: string, bookingId: string): Promise<CourtBookingDto> {
  return apiFetch<CourtBookingDto>(`${GROUPS.V1.BOOKINGS}/${bookingId}`, { token });
}

export async function createWalkInBooking(token: string, payload: CreateWalkInBookingRequest, idempotencyKey?: string): Promise<CourtBookingDto> {
  return apiFetch<CourtBookingDto>(`${GROUPS.V1.BOOKINGS}/walk-in`, { token, method: "POST", body: payload, headers: idempotencyHeaders(idempotencyKey) });
}

export async function cancelBooking(token: string, bookingId: string, payload: CancelBookingRequest, idempotencyKey?: string): Promise<CourtBookingDto> {
  return apiFetch<CourtBookingDto>(`${GROUPS.V1.BOOKINGS}/${bookingId}/cancel`, { token, method: "POST", body: payload, headers: idempotencyHeaders(idempotencyKey) });
}

export async function completeBooking(token: string, bookingId: string, idempotencyKey?: string): Promise<CourtBookingDto> {
  return apiFetch<CourtBookingDto>(`${GROUPS.V1.BOOKINGS}/${bookingId}/complete`, { token, method: "POST", headers: idempotencyHeaders(idempotencyKey) });
}

export async function markBookingNoShow(token: string, bookingId: string, idempotencyKey?: string): Promise<CourtBookingDto> {
  return apiFetch<CourtBookingDto>(`${GROUPS.V1.BOOKINGS}/${bookingId}/no-show`, { token, method: "POST", headers: idempotencyHeaders(idempotencyKey) });
}

function idempotencyHeaders(key?: string): Record<string, string> {
  return { "Idempotency-Key": key ?? newIdempotencyKey() };
}

export async function openCashSession(token: string, payload: OpenCashSessionRequest, idempotencyKey?: string): Promise<CashSessionDto> {
  return apiFetch<CashSessionDto>(`${GROUPS.V1.CASH_SESSIONS}/open`, {
    token,
    method: "POST",
    body: payload,
    headers: idempotencyHeaders(idempotencyKey),
  });
}

export async function getCurrentCashSession(token: string): Promise<CashSessionDto | null> {
  try {
    return await apiFetch<CashSessionDto>(`${GROUPS.V1.CASH_SESSIONS}/current`, { token });
  } catch (e) {
    if (e instanceof ApiError && e.status === 404) return null;
    throw e;
  }
}

export async function recordCashTransaction(token: string, payload: RecordCashTransactionRequest, idempotencyKey?: string): Promise<CashTransactionDto> {
  return apiFetch<CashTransactionDto>(`${GROUPS.V1.CASH_SESSIONS}/current/transactions`, {
    token,
    method: "POST",
    body: payload,
    headers: idempotencyHeaders(idempotencyKey),
  });
}

export async function closeCashSession(token: string, payload: CloseCashSessionRequest, idempotencyKey?: string): Promise<CashSessionDto> {
  return apiFetch<CashSessionDto>(`${GROUPS.V1.CASH_SESSIONS}/current/close`, {
    token,
    method: "POST",
    body: payload,
    headers: idempotencyHeaders(idempotencyKey),
  });
}

export async function getCashSessionDetails(token: string, cashSessionId: string): Promise<CashSessionDetailsDto> {
  return apiFetch<CashSessionDetailsDto>(`${GROUPS.V1.CASH_SESSIONS}/${cashSessionId}`, { token });
}

export async function createQuickSale(token: string, payload: CreateQuickSaleRequest, idempotencyKey?: string): Promise<SaleDto> {
  return apiFetch<SaleDto>(`${GROUPS.V1.SALES}/quick`, {
    token,
    method: "POST",
    body: payload,
    headers: idempotencyHeaders(idempotencyKey),
  });
}

export interface ListTabsParams {
  status?: number;
  page?: number;
  pageSize?: number;
}

export async function listTabs(token: string, params: ListTabsParams = {}): Promise<PagedResultOfSaleDto> {
  return apiFetch<PagedResultOfSaleDto>(`${GROUPS.V1.SALES}/tabs${toQuery(params)}`, { token });
}

export async function openTab(token: string, payload: OpenTabRequest, idempotencyKey?: string): Promise<SaleDto> {
  return apiFetch<SaleDto>(`${GROUPS.V1.SALES}/tabs`, {
    token,
    method: "POST",
    body: payload,
    headers: idempotencyHeaders(idempotencyKey),
  });
}

export async function getTabDetails(token: string, saleId: string): Promise<TabDetailsDto> {
  return apiFetch<TabDetailsDto>(`${GROUPS.V1.SALES}/tabs/${saleId}`, { token });
}

export async function addItemToTab(token: string, saleId: string, payload: AddItemToTabRequest, idempotencyKey?: string): Promise<SaleDto> {
  return apiFetch<SaleDto>(`${GROUPS.V1.SALES}/tabs/${saleId}/items`, {
    token,
    method: "POST",
    body: payload,
    headers: idempotencyHeaders(idempotencyKey),
  });
}

export async function removeItemFromTab(token: string, saleId: string, itemId: string, idempotencyKey?: string): Promise<SaleDto> {
  return apiFetch<SaleDto>(`${GROUPS.V1.SALES}/tabs/${saleId}/items/${itemId}`, {
    token,
    method: "DELETE",
    headers: idempotencyHeaders(idempotencyKey),
  });
}

export async function closeTab(token: string, saleId: string, payload: CloseTabRequest, idempotencyKey?: string): Promise<SaleDto> {
  return apiFetch<SaleDto>(`${GROUPS.V1.SALES}/tabs/${saleId}/close`, {
    token,
    method: "POST",
    body: payload,
    headers: idempotencyHeaders(idempotencyKey),
  });
}

export interface ListExpensesParams {
  status?: number;
  category?: number;
  dueDateFrom?: string;
  dueDateTo?: string;
  recurrenceGroupId?: string;
  page?: number;
  pageSize?: number;
}

export async function listExpenses(token: string, params: ListExpensesParams = {}): Promise<PagedResultOfExpenseDto> {
  return apiFetch<PagedResultOfExpenseDto>(`${GROUPS.V1.EXPENSES}${toQuery(params)}`, { token });
}

export async function getExpense(token: string, expenseId: string): Promise<ExpenseDto> {
  return apiFetch<ExpenseDto>(`${GROUPS.V1.EXPENSES}/${expenseId}`, { token });
}

export async function createExpense(token: string, payload: CreateExpenseRequest, idempotencyKey?: string): Promise<CreateExpenseResultDto> {
  return apiFetch<CreateExpenseResultDto>(GROUPS.V1.EXPENSES, {
    token,
    method: "POST",
    body: payload,
    headers: idempotencyHeaders(idempotencyKey),
  });
}

export async function updateExpense(token: string, expenseId: string, payload: UpdateExpenseRequest, idempotencyKey?: string): Promise<ExpenseDto> {
  return apiFetch<ExpenseDto>(`${GROUPS.V1.EXPENSES}/${expenseId}`, {
    token,
    method: "PATCH",
    body: payload,
    headers: idempotencyHeaders(idempotencyKey),
  });
}

export async function payExpense(token: string, expenseId: string, payload: PayExpenseRequest, idempotencyKey?: string): Promise<ExpenseDto> {
  return apiFetch<ExpenseDto>(`${GROUPS.V1.EXPENSES}/${expenseId}/pay`, {
    token,
    method: "PATCH",
    body: payload,
    headers: idempotencyHeaders(idempotencyKey),
  });
}

export async function cancelExpense(token: string, expenseId: string, idempotencyKey?: string): Promise<ExpenseDto> {
  return apiFetch<ExpenseDto>(`${GROUPS.V1.EXPENSES}/${expenseId}/cancel`, {
    token,
    method: "PATCH",
    headers: idempotencyHeaders(idempotencyKey),
  });
}

export async function deleteExpense(token: string, expenseId: string, idempotencyKey?: string): Promise<void> {
  await apiFetch<unknown>(`${GROUPS.V1.EXPENSES}/${expenseId}`, {
    token,
    method: "DELETE",
    headers: idempotencyHeaders(idempotencyKey),
  });
}

export async function updateExpenseSeries(token: string, groupId: string, payload: UpdateExpenseSeriesRequest, idempotencyKey?: string): Promise<ExpenseDto[]> {
  return apiFetch<ExpenseDto[]>(`${GROUPS.V1.EXPENSES}/recurrence/${groupId}`, {
    token,
    method: "PATCH",
    body: payload,
    headers: idempotencyHeaders(idempotencyKey),
  });
}

export async function cancelExpenseSeries(token: string, groupId: string, idempotencyKey?: string): Promise<ExpenseDto[]> {
  return apiFetch<ExpenseDto[]>(`${GROUPS.V1.EXPENSES}/recurrence/${groupId}/cancel`, {
    token,
    method: "PATCH",
    headers: idempotencyHeaders(idempotencyKey),
  });
}

export interface FinancialSummaryParams {
  month?: number;
  year?: number;
}

export async function getFinancialSummary(token: string, params: FinancialSummaryParams = {}): Promise<FinancialSummaryDto> {
  return apiFetch<FinancialSummaryDto>(`${GROUPS.V1.FINANCIALS}/summary${toQuery(params)}`, { token });
}

export async function getTenantFinancialSettings(token: string): Promise<TenantFinancialSettingsDto> {
  return apiFetch<TenantFinancialSettingsDto>(GROUPS.V1.FINANCIAL_SETTINGS, { token });
}

export async function updateTenantFinancialSettings(
  token: string,
  payload: UpdateTenantFinancialSettingsRequest,
  idempotencyKey?: string,
): Promise<TenantFinancialSettingsDto> {
  return apiFetch<TenantFinancialSettingsDto>(GROUPS.V1.FINANCIAL_SETTINGS, {
    token,
    method: "PUT",
    body: payload,
    headers: idempotencyHeaders(idempotencyKey),
  });
}

export interface MonthlyProfitTrendParams {
  months?: number;
}

export async function getMonthlyProfitTrend(token: string, params: MonthlyProfitTrendParams = {}): Promise<MonthlyProfitPointDto[]> {
  return apiFetch<MonthlyProfitPointDto[]>(`${GROUPS.V1.FINANCIALS}/monthly-trend${toQuery(params)}`, { token });
}

export async function getMarginByClass(token: string): Promise<MarginByClassDto[]> {
  return apiFetch<MarginByClassDto[]>(`${GROUPS.V1.FINANCIALS}/margin-by-class`, { token });
}

export async function listOverdueMembershipPayments(token: string): Promise<OverdueMembershipPaymentDto[]> {
  return apiFetch<OverdueMembershipPaymentDto[]>(`${GROUPS.V1.MEMBER_PAYMENTS}/overdue`, { token });
}

export interface ListTenantMembershipPaymentsParams {
  status?: number;
  page?: number;
  pageSize?: number;
}

export async function listTenantMembershipPayments(
  token: string,
  params: ListTenantMembershipPaymentsParams = {},
): Promise<PagedResultOfTenantMembershipPaymentDto> {
  return apiFetch<PagedResultOfTenantMembershipPaymentDto>(`${GROUPS.V1.MEMBER_PAYMENTS}${toQuery(params)}`, { token });
}

export async function payMembershipPayment(
  token: string,
  paymentId: string,
  payload: { paymentMethod: number },
  idempotencyKey?: string,
): Promise<MembershipPaymentDto> {
  return apiFetch<MembershipPaymentDto>(`${GROUPS.V1.MEMBER_PAYMENTS}/${paymentId}/pay`, {
    token,
    method: "PATCH",
    body: payload,
    headers: idempotencyHeaders(idempotencyKey),
  });
}

export async function cancelMembershipPayment(
  token: string,
  paymentId: string,
  idempotencyKey?: string,
): Promise<MembershipPaymentDto> {
  return apiFetch<MembershipPaymentDto>(`${GROUPS.V1.MEMBER_PAYMENTS}/${paymentId}/cancel`, {
    token,
    method: "PATCH",
    headers: idempotencyHeaders(idempotencyKey),
  });
}

export interface ListTasksParams {
  status?: number;
  assigneeUserId?: string;
  overdueOnly?: boolean;
  page?: number;
  pageSize?: number;
}

export interface DailyCheckInStatusesParams {
  date?: string;
  page?: number;
  pageSize?: number;
}

export async function getDailyCheckInStatuses(token: string, params: DailyCheckInStatusesParams = {}): Promise<PagedResultOfDailyCheckInStatusDto> {
  return apiFetch<PagedResultOfDailyCheckInStatusDto>(`${GROUPS.V1.CHECK_INS}/daily-statuses${toQuery(params)}`, { token });
}

export async function getCheckInSummary(token: string, params: { date?: string } = {}): Promise<CheckInSummaryDto> {
  return apiFetch<CheckInSummaryDto>(`${GROUPS.V1.CHECK_INS}/summary${toQuery(params)}`, { token });
}

export async function createCheckIn(token: string, payload: CheckInRequest, idempotencyKey?: string): Promise<CheckInDto> {
  return apiFetch<CheckInDto>(GROUPS.V1.CHECK_INS, { token, method: "POST", body: payload, headers: idempotencyHeaders(idempotencyKey) });
}

export async function listTasks(token: string, params: ListTasksParams = {}): Promise<PagedResultOfTaskItemDto> {
  return apiFetch<PagedResultOfTaskItemDto>(`${GROUPS.V1.TASKS}${toQuery(params)}`, { token });
}

export async function createTask(token: string, payload: CreateTaskItemRequest, idempotencyKey?: string): Promise<TaskItemDto> {
  return apiFetch<TaskItemDto>(GROUPS.V1.TASKS, {
    token,
    method: "POST",
    body: payload,
    headers: idempotencyHeaders(idempotencyKey),
  });
}

export async function updateTask(token: string, taskId: string, payload: UpdateTaskItemRequest, idempotencyKey?: string): Promise<TaskItemDto> {
  return apiFetch<TaskItemDto>(`${GROUPS.V1.TASKS}/${taskId}`, {
    token,
    method: "PATCH",
    body: payload,
    headers: idempotencyHeaders(idempotencyKey),
  });
}

export async function deleteTask(token: string, taskId: string, idempotencyKey?: string): Promise<void> {
  await apiFetch<unknown>(`${GROUPS.V1.TASKS}/${taskId}`, {
    token,
    method: "DELETE",
    headers: idempotencyHeaders(idempotencyKey),
  });
}

export interface ListPatchNotesParams {
  target?: "panel" | "mobile";
  page?: number;
  pageSize?: number;
}

export async function listPatchNotes(token: string, params: ListPatchNotesParams = {}): Promise<PagedResultOfPatchNoteDto> {
  return apiFetch<PagedResultOfPatchNoteDto>(`${GROUPS.V1.PATCH_NOTES}${toQuery(params)}`, { token });
}

export async function getPatchNote(token: string, id: string): Promise<PatchNoteDto> {
  return apiFetch<PatchNoteDto>(`${GROUPS.V1.PATCH_NOTES}/${id}`, { token });
}

export interface ListNotificationsParams {
  page?: number;
  pageSize?: number;
}

export async function listNotifications(token: string, params: ListNotificationsParams = {}): Promise<NotificationsPageDto> {
  return apiFetch<NotificationsPageDto>(`${GROUPS.V1.NOTIFICATIONS}${toQuery(params)}`, { token });
}

export async function markNotificationsRead(token: string): Promise<MarkNotificationsReadResult> {
  return apiFetch<MarkNotificationsReadResult>(`${GROUPS.V1.NOTIFICATIONS}/read`, { token, method: "POST" });
}
